import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Linkedin, Mail, Instagram, Loader2 } from "lucide-react";
import { fetchPublicTeamMembers } from "@/lib/publicApi";

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetchPublicTeamMembers()
      .then((data) => {
        if (isMounted) setTeamMembers(data);
      })
      .catch(() => {
        if (isMounted) setError(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section id="team" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Struktur Organisasi
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our <span className="text-gradient-blue-orange">Ransel Pro Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Struktur organisasi yang solid dengan kolaborasi lintas bidang untuk menghasilkan karya terbaik.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Memuat tim...
            </div>
          ) : error ? (
            <div className="text-center py-20 text-gray-500">
              Gagal memuat data tim. Silakan coba lagi nanti.
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-20 text-gray-500">Belum ada data tim.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <Card key={member.id} className="group card-hover border-0 bg-white shadow-lg overflow-hidden">
                  <div className="relative">
                    <img
                      src={member.image_url}
                      alt={`${member.name} - ${member.position || ""}`}
                      className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="flex space-x-3 justify-center">
                          {member.social_links?.linkedin && (
                            <a href={member.social_links.linkedin} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-200">
                              <Linkedin className="w-5 h-5 text-white" />
                            </a>
                          )}
                          {member.social_links?.instagram && (
                            <a href={member.social_links.instagram} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-200">
                              <Instagram className="w-5 h-5 text-white" />
                            </a>
                          )}
                          {member.social_links?.email && (
                            <a href={`mailto:${member.social_links.email}`} className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-200">
                              <Mail className="w-5 h-5 text-white" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    {member.position && (
                      <Badge variant="outline" className="mb-3 border-blue-200 text-blue-700">
                        {member.position}
                      </Badge>
                    )}
                    {member.speciality && (
                      <p className="text-sm font-medium text-orange-600 mb-3">{member.speciality}</p>
                    )}
                    {member.bio && (
                      <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
